import { useEffect, useRef } from "react";
import $ from "jquery";
import * as momentModule from "moment";
import { RiCalendarLine } from "react-icons/ri";
import { DISPLAY_DATE_FORMAT_PICKER_MOMENT } from "../../utils/dateUtils";

// Ensure jQuery & moment are available globally for the CJS daterangepicker plugin
window.$ = window.jQuery = $;
const moment = momentModule.default || momentModule;
window.moment = moment; // 🔥 REQUIRED FOR bootstrap-daterangepicker

// CSS can be imported statically
import "bootstrap-daterangepicker/daterangepicker.css";
import "./daterange.css"; // Custom overrides

const DateRangePicker = ({
  value,
  onChange,
  placeholder = "Select date range",
  minDate,
  maxDate,
  className = "",
  showIcon = false,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const setupDateRangePicker = async () => {
      // Dynamically import the CJS plugin *after* globals are set
      await import("bootstrap-daterangepicker");

      if (!isMounted || !inputRef.current) return;

      const $input = $(inputRef.current);

      $input.daterangepicker(
        {
          autoUpdateInput: false,
          drops: "down", // Always drop below the input field
          opens: "center", // Center the picker relative to the input

          // Default dates (Today)
          startDate: value?.startDate ? moment(value.startDate) : moment(),
          endDate: value?.endDate ? moment(value.endDate) : moment(),
          minDate: minDate ? moment(minDate) : undefined,
          maxDate: maxDate ? moment(maxDate) : undefined,
          locale: {
            cancelLabel: "Clear",
            format: DISPLAY_DATE_FORMAT_PICKER_MOMENT,
          },
          // Preset range options shown as buttons
          ranges: {
            Today: [moment(), moment()],
            Yesterday: [moment().subtract(1, "day"), moment().subtract(1, "day")],
            "Last 7 Days": [moment().subtract(6, "days"), moment()],
            "Last Week": [
              moment().subtract(1, "week").startOf("week"),
              moment().subtract(1, "week").endOf("week"),
            ],
            "This Month": [moment().startOf("month"), moment().endOf("month")],
            "Last Month": [
              moment().subtract(1, "month").startOf("month"),
              moment().subtract(1, "month").endOf("month"),
            ],
          },
          showCustomRangeLabel: true, // show built‑in "Custom Range" option
          // let the picker decide when to show calendars (typically when Custom Range is used)
          alwaysShowCalendars: false,
        },
        function (start, end) {
          // Keep input text and external state in sync for all selections
          $input.val(
            `${start.format(DISPLAY_DATE_FORMAT_PICKER_MOMENT)} - ${end.format(DISPLAY_DATE_FORMAT_PICKER_MOMENT)}`
          );
          onChange({
            startDate: start.format("YYYY-MM-DD"),
            endDate: end.format("YYYY-MM-DD"),
          });
        }
      );

      // Set default visible text based on initial dates (Today by default)
      const initialStart = value?.startDate ? moment(value.startDate) : moment();
      const initialEnd = value?.endDate ? moment(value.endDate) : moment();
      $input.val(
        `${initialStart.format(DISPLAY_DATE_FORMAT_PICKER_MOMENT)} - ${initialEnd.format(DISPLAY_DATE_FORMAT_PICKER_MOMENT)}`
      );

      $input.on("apply.daterangepicker", function (ev, picker) {
        $(this).val(
          `${picker.startDate.format(DISPLAY_DATE_FORMAT_PICKER_MOMENT)} - ${picker.endDate.format(DISPLAY_DATE_FORMAT_PICKER_MOMENT)}`
        );

        onChange({
          startDate: picker.startDate.format("YYYY-MM-DD"),
          endDate: picker.endDate.format("YYYY-MM-DD"),
        });
      });

      $input.on("cancel.daterangepicker", function () {
        $(this).val("");
        onChange(null);
      });
    };

    setupDateRangePicker();

    return () => {
      isMounted = false;
      const $input = $(inputRef.current);
      $input.data("daterangepicker")?.remove();
    };
  }, []);

  // Keep input + picker in sync when parent value changes (e.g. Filter Reset → today)
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const $input = $(el);
    const drp = $input.data("daterangepicker");
    if (!drp) return;

    const start = value?.startDate ? moment(value.startDate) : moment();
    const end = value?.endDate ? moment(value.endDate) : moment();
    if (typeof drp.setStartDate === "function") {
      drp.setStartDate(start);
      drp.setEndDate(end);
    } else {
      drp.startDate = start;
      drp.endDate = end;
    }
    $input.val(
      `${start.format(DISPLAY_DATE_FORMAT_PICKER_MOMENT)} - ${end.format(DISPLAY_DATE_FORMAT_PICKER_MOMENT)}`
    );
  }, [value?.startDate, value?.endDate]);

  return (
    <div className="relative w-full flex items-center">
      {showIcon && (
        <RiCalendarLine
          size={18}
          className="text-gray-500 absolute left-3 pointer-events-none z-10"
        />
      )}
      <input
        ref={inputRef}
        type="text"
        readOnly
        placeholder={placeholder}
        className={`w-full py-2 text-sm border border-gray-300 rounded-md h-9
          focus:outline-none focus:ring-2 focus:ring-blue-500
          bg-white cursor-pointer ${showIcon ? "pl-10 pr-3" : "px-3"} ${className}`}
      />
    </div>
  );
};

export default DateRangePicker;
